import type { AccessStatus, PlanChoice } from './types';

const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** 12345 → "R$ 123,45". */
export const formatMoney = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : money.format(cents / 100);

/** "2026-10-18" (a day, no time) → "18/10/2026". */
export function formatDueDate(day: string | null | undefined) {
  if (!day) return '—';
  const [y, m, d] = day.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** An instant → "18/10/2026" in the user's timezone. */
export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

type ChipColor = 'success' | 'primary' | 'warning' | 'error' | 'default' | 'lime' | 'info';

export const STATUS: Record<AccessStatus, { label: string; color: ChipColor; hint: string }> = {
  courtesy: {
    label: 'Cortesia',
    color: 'lime',
    hint: 'Acesso liberado pela administração, sem cobrança.',
  },
  trial: { label: 'Período de teste', color: 'info', hint: 'Grátis até a primeira cobrança.' },
  active: { label: 'Em dia', color: 'success', hint: 'Pagamentos em dia.' },
  overdue: {
    label: 'Pagamento pendente',
    color: 'warning',
    hint: 'Há uma fatura vencida. O acesso continua por alguns dias.',
  },
  canceled: {
    label: 'Cancelado',
    color: 'default',
    hint: 'Sem novas cobranças. O acesso vai até o fim do período pago.',
  },
  pending: {
    label: 'Aguardando pagamento',
    color: 'warning',
    hint: 'O acesso começa quando a primeira fatura for paga.',
  },
  expired: {
    label: 'Vencido',
    color: 'error',
    hint: 'O período pago terminou. Pague a fatura em aberto ou contrate um plano.',
  },
  ended: {
    label: 'Cortesia encerrada',
    color: 'error',
    hint: 'A cortesia terminou. Para continuar, contrate um plano.',
  },
  none: { label: 'Sem plano', color: 'default', hint: 'Ainda não contratou um plano.' },
  blocked: {
    label: 'Suspenso',
    color: 'error',
    hint: 'A administração suspendeu o acesso.',
  },
};

export const PAYMENT_STATUS: Record<string, { label: string; color: ChipColor }> = {
  PENDING: { label: 'Em aberto', color: 'warning' },
  OVERDUE: { label: 'Vencida', color: 'error' },
  RECEIVED: { label: 'Paga', color: 'success' },
  CONFIRMED: { label: 'Paga', color: 'success' },
  RECEIVED_IN_CASH: { label: 'Paga', color: 'success' },
  REFUNDED: { label: 'Estornada', color: 'default' },
  REFUND_REQUESTED: { label: 'Estorno pedido', color: 'default' },
  REFUND_IN_PROGRESS: { label: 'Estornando', color: 'default' },
  DELETED: { label: 'Removida', color: 'default' },
};

export const isOpen = (status: string) => status === 'PENDING' || status === 'OVERDUE';

export const ORGANIZATIONS: Record<string, string> = {
  faculdade: 'Faculdade',
  centro_universitario: 'Centro universitário',
  universidade: 'Universidade',
  instituto_federal: 'Instituto federal',
  escola_tecnica: 'Escola técnica',
};

export const CATEGORIES: Record<string, string> = {
  privada_com_fins: 'Privada com fins lucrativos',
  privada_sem_fins: 'Privada sem fins lucrativos',
  publica_federal: 'Pública federal',
  publica_estadual: 'Pública estadual',
  publica_municipal: 'Pública municipal',
  especial: 'Especial (art. 242 da Constituição)',
};

export const STATES =
  'AC AL AM AP BA CE DF ES GO MA MG MS MT PA PB PE PI PR RJ RN RO RR RS SC SE SP TO'.split(' ');

export const CYCLE_SHORT: Record<string, string> = {
  monthly: 'por mês',
  semiannual: 'por semestre',
  annual: 'por ano',
  once: 'pagamento único',
  courtesy: 'cortesia',
};

// The plan picked on the public plans page, kept while the person signs in.
const INTENT = 'lousa.plan-intent';

export function savePlanIntent(choice: PlanChoice) {
  try {
    sessionStorage.setItem(INTENT, JSON.stringify(choice));
  } catch {
    /* private mode: they pick again */
  }
}

export function readPlanIntent(): PlanChoice | null {
  try {
    const raw = sessionStorage.getItem(INTENT);
    return raw ? (JSON.parse(raw) as PlanChoice) : null;
  } catch {
    return null;
  }
}

export function clearPlanIntent() {
  try {
    sessionStorage.removeItem(INTENT);
  } catch {
    /* nothing to clear */
  }
}
